<template>
    <v-container :class="[{ 'no-padding': routine || list }]">
        <div :class="[{ 'custom-title': customTitleStyle }, 'title-page']">
            {{ title }}
        </div>
        <div v-if="list" class="mu-search">
            <kimsa-text-field :value="search" @change="search = $event" dense clearable no-elevation
                :placeholder="$t('common.search')" prepend-inner-icon="mdi-magnify" />
        </div>
        <skeleton-loader v-if="loading" type="agenda-cards" :count="list ? 3 : 1" />
        <div v-else-if="items.length === 0" class="caption text-center mt-6">
            {{ list && search ? $t('common.no_results') : noItems }}
        </div>
        <div v-else-if="list" class="mu-list">
            <div v-for="(item, index) in items" :key="item.objectID || index" class="mu-row">
                <span class="mu-row-badge">{{ index + 1 }}</span>
                <div class="mu-row-info">
                    <div class="mu-row-name">{{ item.name }}</div>
                    <div v-if="metaFor(item)" class="mu-row-sub">{{ metaFor(item) }}</div>
                </div>
                <button-tooltip v-if="useDisabled" top :tooltip="useTooltip">
                    <template v-slot:activator="{ on, attrs }">
                        <span v-on="on" v-bind="attrs" class="mu-use-wrap">
                            <button type="button" class="mu-use mu-use--disabled" disabled>
                                {{ $t('common.use') }}
                            </button>
                        </span>
                    </template>
                </button-tooltip>
                <button v-else type="button" class="mu-use" @click="onClickUse(item)">{{ $t('common.use') }}</button>
            </div>
        </div>
        <div v-else class="car">
            <v-carousel v-model="currentSlide" hide-delimiters :show-arrows="false" height="118">
                <v-carousel-item v-for="(item, index) in items" :key="index">
                    <div class="car-cell">
                        <!-- BLOCKS -->
                        <template v-if="type === 'blocks'">
                            <block-card :block="item" :badge="index + 1" :show-hover="showBlockHover" withMenu
                                @click:edit="onEditBlock(item)" @click:copy="confirmCopy(item)"
                                @click:delete="showDeleteBlock(item.objectID)" />
                        </template>
                        <!-- ROUTINES -->
                        <template v-if="type === 'routines'">
                            <routine-card :routine="item" :badge="index + 1" @click="goToRoutine(item)" withMenu
                                @click:assign="showAssignRoutineDialog(item)"
                                @click:delete="showDeleteRoutine(item.objectID)" @click:folder="showSaveFolder(item)"
                                @click:edit="showEditRoutine(item)" @click:duplicated="onDuplicatedRoutine" />
                        </template>
                        <!-- PROGRAMS -->
                        <template v-if="type === 'programs'">
                            <program-card :program="item" :badge="index + 1" @click="goToProgram(item)" withMenu
                                @click:assign="showAssignProgramDialog(item)" @click:copy="confirmCopy(item)"
                                @click:delete="showDeleteProgram(item.objectID)" @click:folder="showSaveFolder(item)"
                                @click:edit="showEditProgramDialog(item)" />
                        </template>
                        <!-- PRESCRIPTIONS -->
                        <template v-if="type === 'prescriptions'">
                            <prescription-card :prescription="item" :badge="index + 1" withMenu
                                @click:edit="onEditPrescription(item)" @click:copy="confirmCopy(item)"
                                @click:delete="showDeletePrescription(item.objectID)" />
                        </template>
                        <!-- QUESTIONNAIRES -->
                        <template v-if="type === 'questionnaires'">
                            <questionnaire-card :questionnaire="item" :badge="index + 1" withMenu
                                @click:edit="onEditQuestionnaire(item)" @click:copy="confirmCopy(item)"
                                @click:folder="showSaveFolder(item)"
                                @click:delete="showDeleteQuestionnaire(item.objectID)" />
                        </template>
                    </div>
                </v-carousel-item>
            </v-carousel>
            <v-btn v-if="items.length > 1" icon class="ca l" @click="prevSlide">
                <v-icon size="16">mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn v-if="items.length > 1" icon class="ca r" @click="nextSlide">
                <v-icon size="16">mdi-chevron-right</v-icon>
            </v-btn>
        </div>

        <!-- DIALOGS (uno por tipo, fuera del v-for) -->
        <template v-if="type === 'blocks'">
            <kimsa-confirm ref="showDelete" :title="$t('common.delete_block')"
                :content="$t('common.confirm_deleted_block')" :persistent="false" :loading="loadingDelete"
                @confirm="deleteBlock" @cancel="itemId = null" :close-on-confirm="false" :alert-mode="true" />
            <block-editor v-if="type === 'blocks'" ref="blockEditor" :coach-id="coachId" @success="onBlockSaved" />
        </template>
        <template v-if="type === 'routines'">
            <assign-routine-dialog ref="assignRoutine" @hide="itemId = null" :routine-id="itemId"
                :coach-id="coachId" />
            <routine-editor ref="routineEditor" :coach-id="coachId" @success="loadItems" />
            <kimsa-confirm ref="showDelete" :title="$t('routines.delete_routine')"
                :content="$t('routines.confirm_deleted_routine')" :persistent="false" :loading="loadingDelete"
                @confirm="deleteRoutine" @cancel="itemId = null" :close-on-confirm="false" :alert-mode="true" />
            <save-in-folder-dialog ref="saveInFolder" :current-id="itemId" :default-folder="folderId"
                @cancel="itemId = null; folderId = null" type="routines" />
        </template>
        <template v-if="type === 'programs'">
            <save-in-folder-dialog ref="saveInFolder" :current-id="itemId" :default-folder="folderId"
                @cancel="itemId = null; folderId = null" type="programs" />
            <assign-program-dialog ref="assignProgram" @hide="itemId = null" :program-id="itemId"
                :coach-id="coachId" />
            <save-program-dialog ref="saveProgram" editMode />
            <kimsa-confirm ref="showDelete" :title="$t('programs.delete_title')"
                :content="$t('programs.confirm_deleted')" :persistent="false" :loading="loadingDelete"
                @confirm="deleteProgram" @cancel="itemId = null" :close-on-confirm="false" :alert-mode="true" />
        </template>
        <template v-if="type === 'prescriptions'">
            <kimsa-confirm ref="showDelete" :title="$t('common.delete_prescription')"
                :content="$t('common.confirm_deleted_prescription')" :persistent="false" :loading="loadingDelete"
                @confirm="deletePrescription" @cancel="itemId = null" :close-on-confirm="false" :alert-mode="true" />
            <create-prescription-dialog ref="createPrescriptionDialog" :coachId="coachId" />
        </template>
        <template v-if="type === 'questionnaires'">
            <kimsa-confirm ref="showDelete" :title="$t('questionnaires.delete_title')"
                :content="$t('questionnaires.confirm_deleted')" :persistent="false" :loading="loadingDelete"
                @confirm="deleteQuestionnaire" @cancel="itemId = null" :close-on-confirm="false" :alert-mode="true" />
            <create-questionnaire-dialog ref="createQuestionnaireDialog" :coachId="coachId" />
            <save-in-folder-dialog ref="saveInFolder" :current-id="itemId" :default-folder="folderId"
                @cancel="itemId = null; folderId = null" type="questionnaires" />
        </template>

        <kimsa-confirm ref="confirmCopy" :title="$t('common.duplicate')" :content="copyConfirmContent"
            :persistent="false" :loading="loadingCopy" @confirm="onConfirmCopy" @cancel="copyItem = null"
            :close-on-confirm="false" />

        <base-dialog :loading="loading" :value="useDialog" :title="useItem ? useItem.name : $t('common.use')" :width="420"
            @input="useDialog = $event" @close="useDialog = false">
            <kimsa-combobox :placeholder="$t('exercises.pick_block')" dense :items="blocksOptions"
                @change="block = $event" placeholder-class :value="block" ref="block" />
            <template v-if="isPrescription">
                <div v-if="blockWarning" class="use-warning">
                    <v-icon size="14">mdi-information-outline</v-icon>
                    <span>{{ blockWarning }}</span>
                </div>
                <kimsa-combobox v-else class="mt-3" :placeholder="$t('exercises.pick_exercise')"
                    dense :items="exercisesOptions" @change="exercise = $event" placeholder-class :value="exercise"
                    ref="exercise" :readonly="!block" />
            </template>
            <template v-slot:footer>
                <v-btn block depressed color="primary" class="black--text no-radius" x-large :disabled="disabled"
                    @click="onConfirmUse">
                    {{ $t('common.use') }}
                </v-btn>
            </template>
        </base-dialog>
    </v-container>
</template>

<script>

import { db, Timestamp } from '@/utils/Firebase'
import { BlockIndex, RoutineIndex, PrescriptionIndex, QuestionnaireIndex, ProgramIndex } from '@/utils/Algolia'
import { debounce } from '@/utils/Common'
import KimsaTextField from '../common/inputs/KimsaTextField.vue';
import ButtonTooltip from '@/components/common/ButtonTooltip'
import BaseDialog from '../common/BaseDialog.vue';
import RoutineCard from '../common/routines/RoutineCard.vue';
import ProgramCard from '../common/routines/ProgramCard.vue';
import SkeletonLoader from '../common/SkeletonLoader.vue';
import BlockCard from '../common/routines/BlockCard.vue';
import PrescriptionCard from '../common/routines/PrescriptionCard.vue';
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import { getPayloadBlock, exerciseSetsLabel } from '../../utils/Exercises';
import KimsaConfirm from '../common/KimsaConfirm.vue';
import AssignRoutineDialog from './routines/AssignRoutineDialog.vue';
import SaveInFolderDialog from './SaveInFolderDialog.vue';
import AssignProgramDialog from './routines/AssignProgramDialog.vue';
import CreatePrescriptionDialog from './routines/CreatePrescriptionDialog.vue';
import RoutineEditor from './routines/RoutineEditor.vue';
import SaveProgramDialog from './routines/SaveProgramDialog.vue';
import CreateQuestionnaireDialog from './routines/CreateQuestionnaireDialog.vue';
import QuestionnaireCard from '../common/routines/QuestionnaireCard.vue';
import BlockEditor from './routines/BlockEditor.vue';

export default {
    name: "MostUsedCarousel",
    components: { RoutineCard, ProgramCard, SkeletonLoader, BlockCard, PrescriptionCard, KimsaCombobox, KimsaConfirm, AssignRoutineDialog, SaveInFolderDialog, AssignProgramDialog, CreatePrescriptionDialog, RoutineEditor, SaveProgramDialog, CreateQuestionnaireDialog, QuestionnaireCard, BlockEditor, KimsaTextField, BaseDialog, ButtonTooltip },
    props: {
        type: {
            type: String,
            default: null
        },
        list: {
            type: Boolean,
            default: false
        },
        routine: {
            type: Object,
            default: null
        },
        showBlockHover: {
            type: Boolean,
            default: false
        },
        customTitleStyle: {
            type: Boolean,
            default: false
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            currentSlide: 0,
            items: [],
            loading: false,
            block: null,
            exercise: null,
            itemId: null,
            folderId: null,
            loadingDelete: false,
            copyItem: null,
            loadingCopy: false,
            search: '',
            useItem: null,
            useDialog: false,
        };
    },
    computed: {
        algoliaIndex(vm = this) {
            const map = {
                blocks: BlockIndex,
                routines: RoutineIndex,
                prescriptions: PrescriptionIndex,
                questionnaires: QuestionnaireIndex,
                programs: ProgramIndex,
            }
            return map[vm.type] || null
        },
        title(vm = this) {
            return vm.$t(`common.most_${vm.type}_used`)
        },
        noItems(vm = this) {
            return vm.$t(`common.no_most_${vm.type}_used`)
        },
        isPrescription(vm = this) {
            return vm.type === 'prescriptions'
        },
        hasExercises(vm = this) {
            return (vm.routine?.blocks || []).some(block => (block?.exercises || []).length > 0)
        },
        useDisabled(vm = this) {
            if (vm.type === 'questionnaires') return !vm.routine
            if (vm.isPrescription) return !vm.hasExercises
            return false
        },
        useTooltip(vm = this) {
            if (vm.type === 'questionnaires') return vm.$t('routines.save_before_questionnaire')
            return vm.$t('routines.add_exercise_before_use')
        },
        isBlock(vm = this) {
            return vm.type === 'blocks'
        },
        blocksOptions(vm = this) {
            let length = (vm.routine?.blocks?.length || 0)
            if (vm.isBlock) length++
            return Array.from({ length: length }, (_, i) => i + 1)
        },
        exercisesOptions(vm = this) {
            if (!vm.block) return []
            if (vm.selectedBlock?.isCircuit) return []
            return vm.selectedBlock?.exercises || []
        },
        selectedBlock(vm = this) {
            if (!vm.block) return null
            return vm.routine?.blocks?.[vm.block - 1] || null
        },
        firstUsableBlock(vm = this) {
            const blocks = vm.routine?.blocks || []
            const index = blocks.findIndex(block => !block?.isCircuit && (block?.exercises || []).length > 0)
            return index === -1 ? null : index + 1
        },
        blockWarning(vm = this) {
            if (!vm.block) return null
            if (vm.selectedBlock?.isCircuit) return vm.$t('routines.no_prescription_for_circuit')
            if (!vm.exercisesOptions.length) return vm.$t('routines.block_without_exercises')
            return null
        },
        disabled(vm = this) {
            return !vm.block || (vm.isPrescription && !vm.exercise)
        },
        blocksQuantity(vm = this) {
            return vm.routine?.blocks?.length || 0
        },
        copyConfirmContent(vm = this) {
            const keys = {
                blocks: 'common.confirm_duplicate_block',
                programs: 'programs.confirm_duplicate',
                prescriptions: 'common.confirm_duplicate_prescription',
                questionnaires: 'questionnaires.confirm_duplicate',
            }
            return vm.$t(keys[vm.type] || 'common.confirm_duplicate')
        }
    },
    mounted() {
        if (this.type) this.loadItems()
    },
    methods: {
        async loadItems() {
            const vm = this;
            await vm.withLoading(async () => {
                if (vm.list && vm.search && vm.algoliaIndex) {
                    const res = await vm.algoliaIndex.search(vm.search, {
                        filters: `coachId: ${vm.coachId}`,
                        hitsPerPage: 20,
                    })
                    vm.items = res.hits
                } else {
                    let ref = await db.collection(vm.type)
                        .where("coachId", '==', vm.coachId)
                        .orderBy("used", "desc")
                        .limit(5)
                        .get();
                    vm.items = ref.docs.map(doc => ({ ...doc.data(), id: doc.id, objectID: doc.id }))
                }

                vm.currentSlide = 0
            })
        },
        debouncedSearch: debounce(function () {
            this.loadItems()
        }, 400),
        goToRoutine(routine) {
            const vm = this;
            vm.pushRoute({ name: 'coach.routines.detail', params: { uid: routine.id } })
        },
        goToProgram(program) {
            const vm = this;
            vm.pushRoute({ name: 'coach.routines.program.detail', params: { uid: program.id } })
        },
        metaFor(item) {
            const vm = this
            if (vm.type === 'blocks') {
                const ex = item.block?.exercises?.length || 0
                const parts = [`${ex} ${vm.$tc('common.exercises', 2).toLowerCase()}`]
                const min = vm.blockMinutes(item)
                if (min) parts.push(`${min} ${vm.$tc('common.minutes', 2).toLowerCase()}`)
                return parts.join(' · ')
            }
            if (vm.type === 'routines') {
                const blocks = item.blocksQuantity || item.blocks?.length || 0
                const ex = (item.blocks || []).reduce((a, b) => a + (b.exercises?.length || 0), 0)
                const parts = [`${blocks} ${vm.$tc('common.blocks', 2).toLowerCase()}`]
                if (ex) parts.push(`${ex} ${vm.$tc('common.exercises', 2).toLowerCase()}`)
                return parts.join(' · ')
            }
            if (vm.type === 'prescriptions') {
                return exerciseSetsLabel({ config: item.config })
            }
            if (vm.type === 'questionnaires') {
                return `${item.questions?.length || 0} ${vm.$t('questionnaires.questions').toLowerCase()}`
            }
            return ''
        },
        blockMinutes(item) {
            let minutes = 0
            item.block?.exercises?.forEach(exercise => {
                minutes += exercise?.config?.sets?.length || item.block?.sets || 0
            })
            return minutes
        },
        onClickUse(item) {
            const vm = this;
            if (vm.type === 'blocks' || vm.type === 'prescriptions') {
                vm.useItem = item
                vm.block = (vm.isPrescription ? vm.firstUsableBlock : null) || vm.blocksOptions[0] || null
                vm.exercise = null
                if (vm.isBlock && !vm.hasExercises) return vm.onConfirmUse()
                vm.useDialog = true
            } else if (vm.type === 'routines') {
                vm.$emit('use:routine', item)
            } else if (vm.type === 'questionnaires') {
                vm.$emit('use:questionnaire', item)
            }
        },
        async onConfirmUse() {
            const vm = this;
            let item = vm.useItem
            if (item?.objectID) {
                const doc = await db.collection(vm.type).doc(item.objectID).get()
                if (doc.exists) item = { ...doc.data(), id: doc.id, objectID: doc.id }
            }
            vm.$emit('used', { block: vm.block, item, exercise: vm.exercise })
            vm.useDialog = false
        },
        prevSlide() {
            this.currentSlide = this.currentSlide === 0
                ? this.items.length - 1
                : this.currentSlide - 1;
        },
        nextSlide() {
            this.currentSlide = this.currentSlide === this.items.length - 1
                ? 0
                : this.currentSlide + 1;
        },
        showAddBlock() {
            const vm = this;
            vm.$refs.addBlockDialog.show()
        },
        onAddBlock(block) {
            const vm = this;
            vm.$emit('used', { block: vm.block, item: block })
        },
        // BLOCKS MENU
        onEditBlock(block) {
            const vm = this
            vm.$refs.blockEditor.showEdit(block)
        },
        onBlockSaved() {
            const vm = this
            vm.loadItems()
        },
        confirmCopy(item) {
            const vm = this
            vm.copyItem = item
            vm.$refs.confirmCopy.show()
        },
        onConfirmCopy() {
            const vm = this
            const handlers = {
                blocks: vm.onCopyBlock,
                programs: vm.onCopyProgram,
                prescriptions: vm.onCopyPrescription,
                questionnaires: vm.onCopyQuestionnaire,
            }
            if (handlers[vm.type]) handlers[vm.type](vm.copyItem)
        },
        async onCopyBlock(block) {
            let vm = this
            await vm.withLoading(async () => {
                let payload = getPayloadBlock(
                    `${block.name} ${vm.$t('common.copy_template')}`,
                    vm.coachId,
                    block.block,
                    { id: block.folder, name: block.folderName },
                    block?.tags || [],
                    { img: block.img, imgSavePath: block.imgSavePath }
                )

                let newblock = await db.collection('blocks').add(payload)
                vm.toast(vm.$t('common.block_copied'))
                vm.$refs.confirmCopy?.hide()

                vm.$refs.blockEditor.showEdit({ objectID: newblock.id })
            }, 'loadingCopy')
        },
        async showDeleteBlock(id) {
            const vm = this
            vm.itemId = id
            vm.$refs.showDelete.show()
        },
        async deleteBlock() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('blocks').doc(vm.itemId).delete()
                vm.items = vm.items.filter(block => block.objectID !== vm.itemId)
                vm.toast(vm.$t('common.block_deleted'))
                vm.itemId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
        // ROUTINE MENU
        showAssignRoutineDialog(routine) {
            const vm = this;
            vm.itemId = routine.objectID
            vm.$refs.assignRoutine.show()
        },
        async showDeleteRoutine(id) {
            const vm = this
            vm.itemId = id
            vm.$refs.showDelete.show()
        },
        async deleteRoutine() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('routines').doc(vm.itemId).delete()
                vm.items = vm.items.filter(routine => routine.objectID !== vm.itemId)
                vm.toast(vm.$t('routines.deleted'))
                vm.itemId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
        showSaveFolder(routine) {
            const vm = this
            vm.itemId = routine.objectID
            vm.folderId = routine.folder || null
            vm.$refs.saveInFolder.show()
        },
        showEditRoutine(routine) {
            const vm = this;
            vm.$refs.routineEditor.showEdit(routine)
        },
        async onDuplicatedRoutine(routine) {
            const vm = this
            vm.$refs.routineEditor.showEdit(routine)
            await vm.loadItems()
        },
        // PROGRAM MENU
        showAssignProgramDialog(program) {
            const vm = this;
            vm.itemId = program.objectID
            vm.$refs.assignProgram.show()
        },
        async onCopyProgram(program) {
            const vm = this
            await vm.withLoading(async () => {
                let payload = {
                    ...program,
                    coachId: vm.coachId,
                    name: `${program.name} ${vm.$t('common.copy_template')}`
                }
                delete payload.created_at
                delete payload.objectID
                delete payload.id
                payload = JSON.parse(JSON.stringify(payload))
                let ref = await db.collection('programs').add({ ...payload, used: 0, created_at: Timestamp.now() })

                vm.toast(vm.$t('programs.copied'))
                vm.$refs.confirmCopy?.hide()
                vm.pushRoute({ name: 'coach.routines.program.detail', params: { uid: ref.id } })
            }, 'loadingCopy')
        },
        async showDeleteProgram(id) {
            const vm = this
            vm.itemId = id
            vm.$refs.showDelete.show()
        },
        async deleteProgram() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('programs').doc(vm.itemId).delete()
                vm.items = vm.items.filter(program => program.objectID !== vm.itemId)
                vm.toast(vm.$t('programs.deleted'))
                vm.itemId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
        showEditProgramDialog(program) {
            const vm = this;
            vm.$refs.saveProgram.show(program)
        },
        // PRESCRIPTION MENU
        onEditPrescription(prescription) {
            const vm = this
            vm.$refs.createPrescriptionDialog.setEditingMode(prescription)
        },
        async onCopyPrescription(prescription) {
            let vm = this
            await vm.withLoading(async () => {
                let payload = {
                    ...prescription,
                    coachId: vm.coachId,
                    name: `${prescription.name} ${vm.$t('common.copy_template')}`,
                }
                delete payload.objectID
                delete payload.id
                payload = JSON.parse(JSON.stringify(payload))
                let ref = await db.collection('prescriptions').add({ ...payload, used: 0 })
                vm.toast(vm.$t('common.prescription_duplicated'))
                vm.$refs.confirmCopy?.hide()
                vm.$refs.createPrescriptionDialog.setEditingMode({ ...payload, objectID: ref.id, id: ref.id })
            }, 'loadingCopy')
        },
        async showDeletePrescription(id) {
            const vm = this
            vm.itemId = id
            vm.$refs.showDelete.show()
        },
        async deletePrescription() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('prescriptions').doc(vm.itemId).delete()
                vm.items = vm.items.filter(prescription => prescription.objectID !== vm.itemId)
                vm.toast(vm.$t('common.prescription_deleted'))
                vm.itemId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
        // QUESTIONNAIRE MENU
        onEditQuestionnaire(questionnaire) {
            const vm = this
            vm.$refs.createQuestionnaireDialog.setEditingMode(questionnaire)
        },
        async onCopyQuestionnaire(questionnaire) {
            let vm = this
            await vm.withLoading(async () => {
                let payload = {
                    ...questionnaire,
                    coachId: vm.coachId,
                    name: `${questionnaire.name} ${vm.$t('common.copy_template')}`
                }
                delete payload.created_at
                delete payload.objectID
                delete payload.id
                payload = JSON.parse(JSON.stringify(payload))
                let ref = await db.collection('questionnaires').add({ ...payload, used: 0, created_at: Timestamp.now() })
                vm.toast(vm.$t('questionnaires.copied'))
                vm.$refs.confirmCopy?.hide()
                vm.$refs.createQuestionnaireDialog.setEditingMode({ ...payload, objectID: ref.id, id: ref.id })
            }, 'loadingCopy')
        },
        async showDeleteQuestionnaire(id) {
            const vm = this
            vm.itemId = id
            vm.$refs.showDelete.show()
        },
        async deleteQuestionnaire() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('questionnaires').doc(vm.itemId).delete()
                vm.items = vm.items.filter(questionnaire => questionnaire.objectID !== vm.itemId)
                vm.toast(vm.$t('questionnaires.deleted'))
                vm.itemId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
    },
    watch: {
        type(val) {
            if (val) this.loadItems()
        },
        coachId() {
            if (this.type) this.loadItems()
        },
        search() {
            this.debouncedSearch()
        }
    },
};
</script>

<style scoped>
.mu-search {
    margin-bottom: 10px;
}

.mu-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.mu-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    background: var(--v-surface-base);
    cursor: pointer;
    transition: border-color .15s, background .15s;
}

.mu-row:hover {
    border-color: var(--v-outline2-base);
}

.mu-row.selected {
    border-color: var(--v-primary-base);
    background: var(--v-surface2-base);
}

.mu-row-badge {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
    font-size: 11px;
    font-weight: 700;
}

.mu-row-info {
    min-width: 0;
    flex: 1;
}

.mu-row-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mu-row-sub {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mu-use {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    padding: 5px 12px;
    border: none;
    border-radius: 6px;
    background: linear-gradient(135deg, var(--v-primary-base), var(--v-accent-base));
    color: #0C0E12;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .03em;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
    transition: opacity .15s;
}

.mu-row:hover .mu-use,
.mu-use:focus {
    opacity: 1;
}

.use-warning {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 12px;
    font-size: 12px;
    line-height: 1.4;
    color: var(--v-txtSecondary-base);
}

.use-warning .v-icon {
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    margin-top: 1px;
}

.mu-use-wrap {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    opacity: 0;
    transition: opacity .15s;
}

.mu-row:hover .mu-use-wrap {
    opacity: 1;
}

.mu-use-wrap .mu-use {
    position: static;
    transform: none;
    opacity: 1;
}

.mu-use--disabled {
    background: var(--v-surface2-base);
    color: var(--v-txtMuted-base);
    box-shadow: none;
    cursor: not-allowed;
}

.car {
    position: relative;
}

.car-cell {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 38px;
    box-sizing: border-box;
}

.car-cell > * {
    width: 100%;
    min-width: 0;
}

.ca {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 26px !important;
    height: 26px !important;
    min-width: 26px !important;
    border-radius: 50% !important;
    background: var(--v-surface2-base) !important;
    border: 1px solid var(--v-outline-base) !important;
    color: var(--v-txtSecondary-base) !important;
    z-index: 1;
}

.ca:hover {
    background: var(--v-outline-base) !important;
    color: var(--v-txtPrimary-base) !important;
}

.ca.l {
    left: 4px;
}

.ca.r {
    right: 4px;
}

.ca .v-icon {
    color: inherit !important;
}

.title-page {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    margin-bottom: 12px;
}

.custom-title {
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .05em;
    color: var(--v-txtMuted-base);
    background: transparent;
    text-align: left;
    padding: 0;
    margin-top: 12px;
    margin-bottom: 4px;
}
</style>